import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Rocket, Search, RefreshCw, Settings2, ChevronRight, ChevronDown, Save,
  TrendingUp, TrendingDown, Minus, Store, Ban, Check,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const marketLabels = {
  amazon_uae: 'Amazon UAE', amazon_ksa: 'Amazon KSA', amazon_au: 'Amazon AU', noon_uae: 'Noon UAE',
  amazon_ind: 'Amazon India', flipkart: 'Flipkart', myntra: 'Myntra', offline: 'Offline', ajio: 'Ajio',
};

const statusConfig = {
  scale: { label: 'Scale', className: 'bg-green-100 text-green-800 border-green-300' },
  discontinue: { label: 'Discontinue', className: 'bg-red-100 text-red-800 border-red-300' },
  in_progress: { label: 'In Progress', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  not_set: { label: 'Not Set', className: 'bg-gray-100 text-gray-500 border-gray-300' },
  info: { label: 'Info', className: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const StatusBadge = ({ status, testid }) => {
  const c = statusConfig[status] || statusConfig.not_set;
  return <Badge variant="outline" className={`text-xs ${c.className}`} data-testid={testid}>{c.label}</Badge>;
};

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-3.5 h-3.5 text-green-600 inline" />;
  if (trend === 'down') return <TrendingDown className="w-3.5 h-3.5 text-red-600 inline" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400 inline" />;
};

export default function LaunchTrackerPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [window, setWindow] = useState(60);
  const [groupBy, setGroupBy] = useState('color_parent');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [drafts, setDrafts] = useState({}); // cp -> { launch_dates, initial_stock }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(null);

  const intlMarkets = data?.international_markets || ['amazon_uae', 'amazon_ksa', 'amazon_au', 'noon_uae'];
  const indiaMarkets = data?.india_markets || ['amazon_ind', 'flipkart', 'myntra', 'offline', 'ajio'];
  const showVerdict = data?.show_verdict !== false;
  const isColorParent = groupBy === 'color_parent';
  const groupLabel = { color_parent: 'Color Parent', parent_sku: 'Parent SKU', sku: 'Child SKU' }[groupBy] || 'Color Parent';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ window: window.toString(), group_by: groupBy });
      if (search.trim()) params.append('search', search.trim());
      const res = await axios.get(`${API}/launch-tracker?${params}`);
      setData(res.data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load launch tracker', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [window, groupBy, search, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (cp, row) => {
    const next = new Set(expanded);
    if (next.has(cp)) {
      next.delete(cp);
    } else {
      next.add(cp);
      // seed draft from current config
      const ld = {}, is = {};
      intlMarkets.forEach((m) => {
        ld[m] = row.markets[m]?.launch_date || '';
        is[m] = row.markets[m]?.initial_stock ?? '';
      });
      setDrafts((d) => ({ ...d, [cp]: { launch_dates: ld, initial_stock: is } }));
    }
    setExpanded(next);
  };

  const updateDraft = (cp, market, field, value) => {
    setDrafts((d) => ({
      ...d,
      [cp]: { ...d[cp], [field]: { ...d[cp][field], [market]: value } },
    }));
  };

  const saveConfig = async (cp) => {
    const draft = drafts[cp];
    const launch_dates = {};
    intlMarkets.forEach((m) => {
      if (draft.launch_dates[m]) launch_dates[m] = draft.launch_dates[m];
    });
    try {
      await axios.put(`${API}/launch-tracker/config/${encodeURIComponent(cp)}`, { launch_dates });
      toast({ title: 'Saved', description: `Launch dates updated for ${cp}` });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    }
  };

  const [discontinuing, setDiscontinuing] = useState({}); // key -> true while in-flight
  const markDiscontinued = async (row) => {
    const key = row.color_parent;
    setDiscontinuing((d) => ({ ...d, [key]: true }));
    try {
      await axios.post(`${API}/discontinued/mark`, {
        key,
        skus: row.skus || [],
        reason: `Launch Tracker: Discontinue verdict (${groupLabel})`,
        source: 'launch_tracker',
      });
      toast({ title: 'Added to Discontinued', description: `${key} was added to your Discontinued Products.` });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to add to discontinued', variant: 'destructive' });
    } finally {
      setDiscontinuing((d) => ({ ...d, [key]: false }));
    }
  };

  const openSettings = () => { setSettingsDraft({ ...data.settings }); setSettingsOpen(true); };
  const saveSettings = async () => {
    try {
      await axios.put(`${API}/launch-tracker/settings`, {
        launch_window_days: parseInt(settingsDraft.launch_window_days),
        intl_units_threshold: parseInt(settingsDraft.intl_units_threshold),
        overall_sellthrough_target: parseFloat(settingsDraft.overall_sellthrough_target),
        return_rate_cutoff: parseFloat(settingsDraft.return_rate_cutoff),
        return_rule_min_units: parseInt(settingsDraft.return_rule_min_units),
      });
      toast({ title: 'Settings saved' });
      setSettingsOpen(false);
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to save settings', variant: 'destructive' });
    }
  };

  const counts = { scale: 0, discontinue: 0, in_progress: 0, not_set: 0 };
  (data?.rows || []).forEach((r) => { counts[r.overall.status] = (counts[r.overall.status] || 0) + 1; });

  return (
    <Layout>
      <div className="space-y-6" data-testid="launch-tracker-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading flex items-center gap-3">
              <Rocket className="w-8 h-8 text-blue-600" /> Launch Tracker
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Track newly launched products per marketplace and decide whether to scale or discontinue.
              Indian markets are informational; the Scale/Discontinue verdict is based on international markets.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && data && (
              <Button variant="outline" onClick={openSettings} data-testid="launch-settings-btn">
                <Settings2 className="w-4 h-4 mr-2" /> Rules
              </Button>
            )}
            <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="launch-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        {showVerdict && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'scale', label: 'Scale', color: 'text-green-700' },
            { key: 'discontinue', label: 'Discontinue', color: 'text-red-700' },
            { key: 'in_progress', label: 'In Progress', color: 'text-amber-700' },
            { key: 'not_set', label: 'Not Configured', color: 'text-gray-500' },
          ].map((c) => (
            <div key={c.key} className="border border-border rounded-sm p-4" data-testid={`summary-${c.key}`}>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{c.label}</p>
              <p className={`text-3xl font-bold font-mono ${c.color}`}>{counts[c.key] || 0}</p>
            </div>
          ))}
        </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Group by:</Label>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v)}>
              <SelectTrigger className="w-40" data-testid="launch-groupby-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent_sku">Parent SKU</SelectItem>
                <SelectItem value="color_parent">Color Parent</SelectItem>
                <SelectItem value="sku">Child SKU</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Window:</Label>
            <Select value={window.toString()} onValueChange={(v) => setWindow(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="launch-window-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="120">120 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${groupLabel.toLowerCase()} or SKU...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="launch-search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="p-3 w-8"></th>
                <th className="p-3 font-semibold">{groupLabel}</th>
                {showVerdict && <th className="p-3 font-semibold">Overall Verdict</th>}
                {intlMarkets.map((m) => (
                  <th key={m} className="p-3 font-semibold text-center">{marketLabels[m]}</th>
                ))}
                <th className="p-3 font-semibold text-right">SKUs</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).length === 0 && !loading && (
                <tr><td colSpan={(showVerdict ? 4 : 3) + intlMarkets.length} className="p-8 text-center text-muted-foreground">
                  No newly launched products in this window.
                </td></tr>
              )}
              {(data?.rows || []).map((row) => (
                <React.Fragment key={row.color_parent}>
                  <tr
                    className="border-b border-border hover:bg-muted/20 cursor-pointer"
                    onClick={() => toggleExpand(row.color_parent, row)}
                    data-testid={`launch-row-${row.color_parent}`}
                  >
                    <td className="p-3">{expanded.has(row.color_parent) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    <td className="p-3 font-mono font-medium">{row.color_parent}</td>
                    {showVerdict && (
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={row.overall.status} testid={`overall-${row.color_parent}`} />
                          {row.overall.status === 'discontinue' && (
                            row.is_discontinued ? (
                              <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600 border-gray-300" data-testid={`discontinued-done-${row.color_parent}`}>
                                <Check className="w-3 h-3 mr-1" /> In Discontinued
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-xs text-red-700 border-red-300 hover:bg-red-50"
                                disabled={!!discontinuing[row.color_parent]}
                                onClick={(e) => { e.stopPropagation(); markDiscontinued(row); }}
                                data-testid={`add-discontinued-${row.color_parent}`}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                {discontinuing[row.color_parent] ? 'Adding…' : 'Add to Discontinued'}
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                    )}
                    {intlMarkets.map((m) => {
                      const mk = row.markets[m];
                      return (
                        <td key={m} className="p-3 text-center">
                          {mk.configured ? (
                            <div className="flex flex-col items-center gap-1">
                              {showVerdict && <StatusBadge status={mk.status} testid={`market-${m}-${row.color_parent}`} />}
                              <span className="text-xs text-muted-foreground font-mono" data-testid={`market-units-${m}-${row.color_parent}`}>{mk.units_sold}u · {mk.velocity}/d</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">not set</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right font-mono text-muted-foreground">{row.sku_count}</td>
                  </tr>

                  {expanded.has(row.color_parent) && (
                    <tr className="border-b border-border bg-muted/10">
                      <td></td>
                      <td colSpan={(showVerdict ? 3 : 2) + intlMarkets.length} className="p-4">
                        {/* International markets config + metrics */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                            <Store className="w-4 h-4" />
                            {isColorParent
                              ? 'International Markets — set launch date & initial stock'
                              : 'International Markets — metrics (launch date & initial stock editable in Color Parent view)'}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                                  <th className="p-2">Market</th>
                                  <th className="p-2">Launch Date</th>
                                  <th className="p-2">Initial Stock</th>
                                  <th className="p-2 text-right">Units</th>
                                  <th className="p-2 text-right">Velocity/d</th>
                                  <th className="p-2 text-right">Returns</th>
                                  <th className="p-2 text-center">Trend</th>
                                  <th className="p-2 text-center">Days</th>
                                  <th className="p-2 text-center">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {intlMarkets.map((m) => {
                                  const mk = row.markets[m];
                                  const draft = drafts[row.color_parent] || { launch_dates: {}, initial_stock: {} };
                                  return (
                                    <tr key={m} className="border-b border-border/50">
                                      <td className="p-2 font-medium">{marketLabels[m]}</td>
                                      <td className="p-2">
                                        {isColorParent ? (
                                          <Input
                                            type="date"
                                            value={draft.launch_dates[m] || ''}
                                            onChange={(e) => updateDraft(row.color_parent, m, 'launch_dates', e.target.value)}
                                            className="h-8 w-40"
                                            data-testid={`launch-date-${m}-${row.color_parent}`}
                                          />
                                        ) : (
                                          <span className="font-mono text-xs">{mk.launch_date || '—'}</span>
                                        )}
                                      </td>
                                      <td className="p-2">
                                        {isColorParent ? (
                                          <Input
                                            type="number"
                                            min="0"
                                            placeholder="optional"
                                            value={draft.initial_stock[m] ?? ''}
                                            onChange={(e) => updateDraft(row.color_parent, m, 'initial_stock', e.target.value)}
                                            className="h-8 w-24"
                                            data-testid={`initial-stock-${m}-${row.color_parent}`}
                                          />
                                        ) : (
                                          <span className="font-mono text-xs">{mk.initial_stock ?? '—'}</span>
                                        )}
                                      </td>
                                      <td className="p-2 text-right font-mono">{mk.units_sold}</td>
                                      <td className="p-2 text-right font-mono">{mk.velocity}</td>
                                      <td className="p-2 text-right font-mono">
                                        {mk.returns > 0 ? `${mk.returns} (${(mk.return_rate * 100).toFixed(0)}%)` : '0'}
                                      </td>
                                      <td className="p-2 text-center"><TrendIcon trend={mk.trend} /></td>
                                      <td className="p-2 text-center font-mono text-muted-foreground">
                                        {mk.days_since_launch != null ? `${mk.days_since_launch}/${mk.window_days}` : '-'}
                                      </td>
                                      <td className="p-2 text-center">
                                        {mk.configured
                                          ? (showVerdict
                                              ? <StatusBadge status={mk.status} />
                                              : <span className="text-xs text-muted-foreground font-mono">{mk.units_sold}u</span>)
                                          : <span className="text-xs text-muted-foreground italic">-</span>}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          {isColorParent && (
                            <Button size="sm" onClick={() => saveConfig(row.color_parent)} data-testid={`save-config-${row.color_parent}`}>
                              <Save className="w-4 h-4 mr-2" /> Save Launch Config
                            </Button>
                          )}

                          {/* India markets (informational) */}
                          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground pt-2">
                            India Markets (informational) — launched {row.india_launch_date || 'unknown'}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {indiaMarkets.map((m) => {
                              const mk = row.markets[m];
                              return (
                                <Badge key={m} variant="secondary" className="font-mono text-xs">
                                  {marketLabels[m]}: {mk.units_sold}u · {mk.velocity}/d
                                  {mk.returns > 0 ? ` · ${(mk.return_rate * 100).toFixed(0)}% ret` : ''}
                                  {' '}<TrendIcon trend={mk.trend} />
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Settings (Rules) Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Launch Tracker Rules</DialogTitle></DialogHeader>
          {settingsDraft && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Evaluation window (days)</Label>
                  <Input type="number" value={settingsDraft.launch_window_days}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, launch_window_days: e.target.value })}
                    data-testid="setting-window" />
                </div>
                <div>
                  <Label className="text-sm">International units threshold</Label>
                  <Input type="number" value={settingsDraft.intl_units_threshold}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, intl_units_threshold: e.target.value })}
                    data-testid="setting-units" />
                </div>
                <div>
                  <Label className="text-sm">Sell-through target (0–1)</Label>
                  <Input type="number" step="0.05" value={settingsDraft.overall_sellthrough_target}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, overall_sellthrough_target: e.target.value })}
                    data-testid="setting-sellthrough" />
                </div>
                <div>
                  <Label className="text-sm">Return-rate cutoff (0–1)</Label>
                  <Input type="number" step="0.05" value={settingsDraft.return_rate_cutoff}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, return_rate_cutoff: e.target.value })}
                    data-testid="setting-return-cutoff" />
                </div>
                <div>
                  <Label className="text-sm">Min units for return rule</Label>
                  <Input type="number" value={settingsDraft.return_rule_min_units}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, return_rule_min_units: e.target.value })}
                    data-testid="setting-return-min" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} data-testid="save-settings-btn">Save Rules</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
